/**
 * Assigning a support ticket and recording the handover in its timeline.
 *
 * SupportInbox.handleAssign used to be: update `issues.assigned_to`, then post
 * the explicit system comment. A database trigger creates the assignee's
 * in-app bell notification. The handover has no dialog and sends no email.
 *
 * It lives here, with the client injected, rather than inline in the component
 * so the ordering rules can be tested without rendering anything:
 *
 *   1. The assignment is the one step that may fail the whole thing. If the
 *      UPDATE fails nothing else is attempted — no comment, no note, no email
 *      about an assignment that did not happen.
 *   2. After it commits, the system event and optional legacy note are
 *      independent. A failure in either is reported but never undoes the
 *      assignment.
 *   3. The database trigger creates the assignee's bell notification. This
 *      browser path never sends an assignment email.
 */
import type { supabase } from "@/lib/supabase";
import { encodeSupportSystemEvent } from "@/lib/support-system-events";

type AssignClient = Pick<typeof supabase, "from">;

/**
 * Longest note, in characters. The textarea enforces it as a courtesy; the
 * database notification payload should remain concise and readable.
 */
export const ASSIGN_NOTE_MAX_LENGTH = 500;

/**
 * The note as it will be stored and sent: trimmed, capped, "" when there is
 * nothing worth saying. Counts code points so an emoji is never cut in half.
 */
export function normalizeAssignNote(raw: string | null | undefined): string {
  // NUL is stripped because Postgres rejects it in text (IssueChatThread does
  // the same): left in, the note insert would fail while the email still went.
  const trimmed = String(raw ?? "")
    .split("\u0000")
    .join("")
    .replace(/\r\n/g, "\n")
    .trim();
  if (!trimmed) return "";
  // A code point is at most two UTF-16 units, so the first MAX code points
  // always sit in the first 2*MAX units — bound the work before spreading it
  // into an array. (After trim, so leading whitespace cannot eat the cap.)
  return Array.from(trimmed.slice(0, ASSIGN_NOTE_MAX_LENGTH * 2))
    .slice(0, ASSIGN_NOTE_MAX_LENGTH)
    .join("")
    .trim();
}

/**
 * The text stored in the Internal notes tab. A fixed prefix in BOTH languages,
 * not t(): this string is persisted, and anything persisted through t() would
 * stay in whichever language the admin happened to be using — so every staff
 * member would read the language of whoever assigned. Naming both keeps one
 * stored text readable for English- and German-speaking staff alike, with no
 * translation stored. Only the admin's own words follow the prefix.
 * (The sibling "📌 Assigned to X" line is English-only; that one predates this
 * and is kept verbatim because production rows already carry that exact text.)
 */
export function buildAssignNoteComment(
  assigneeName: string,
  note: string
): string {
  return `📌 Note for ${assigneeName} · Notiz für ${assigneeName}: ${note}`;
}

export interface AssignTicketInput {
  ticketId: string;
  /** null = unassign. */
  assigneeId: string | null;
  /** The admin making the change. */
  actorId: string;
  /** Pre-built "📌 Assigned to X" / "📌 Unassigned" system comment. */
  systemComment: string;
  /** Display name of the assignee, for the saved note's prefix. */
  assigneeName: string;
  /** The note as typed; normalised here. Ignored unless assigning to someone else. */
  note?: string | null;
}

/** `not-applicable`: nothing to send (self, unassign) or the ticket moved on. */
export type AssignEmailOutcome = "sent" | "failed" | "not-applicable";

export interface AssignTicketResult {
  /** false = the assignment itself failed and nothing else was attempted. */
  assigned: boolean;
  errorMessage: string | null;
  systemCommentFailed: boolean;
  noteFailed: boolean;
  email: AssignEmailOutcome;
}

/** Did this write fail — including by throwing, which a dropped connection does. */
async function writeFailed(
  write: PromiseLike<{ error: unknown }>
): Promise<boolean> {
  try {
    const { error } = await write;
    return !!error;
  } catch {
    return true;
  }
}

export async function assignTicket(
  client: AssignClient,
  input: AssignTicketInput
): Promise<AssignTicketResult> {
  const { error: assignError } = await client
    .from("issues")
    .update({
      assigned_to: input.assigneeId,
      // null on unassign: assigned_by records who set the CURRENT assignee,
      // and an unassigned ticket has none.
      assigned_by: input.assigneeId === null ? null : input.actorId,
    })
    .eq("id", input.ticketId);
  if (assignError) {
    return {
      assigned: false,
      errorMessage: assignError.message || null,
      systemCommentFailed: false,
      noteFailed: false,
      email: "not-applicable",
    };
  }

  const assigneeId = input.assigneeId;
  const tellsSomeone = assigneeId !== null && assigneeId !== input.actorId;
  const note = tellsSomeone ? normalizeAssignNote(input.note) : "";

  const [systemCommentFailed, noteFailed, email] = await Promise.all([
    // Stamp delivered_at so a system event is un-emailable server-side
    // un-emailable server-side (planReplyDispatch refuses on deliveredAt
    // first), not just safe by construction on the client's call graph.
    writeFailed(
      client.from("issue_comments").insert({
        issue_id: input.ticketId,
        user_id: input.actorId,
        comment: encodeSupportSystemEvent(input.systemComment),
        is_internal: true,
        is_system: true,
        sender_scope: "admin",
        delivered_at: new Date().toISOString(),
      })
    ),
    // No delivered_at here: an internal note never leaves the building
    // (planReplyDispatch refuses is_internal), and stamping it would lock the
    // author out of editing their own note.
    note
      ? writeFailed(
          client.from("issue_comments").insert({
            issue_id: input.ticketId,
            user_id: input.actorId,
            comment: buildAssignNoteComment(input.assigneeName, note),
            is_internal: true,
            sender_scope: "admin",
          })
        )
      : Promise.resolve(false),
    Promise.resolve<AssignEmailOutcome>("not-applicable"),
  ]);

  return {
    assigned: true,
    errorMessage: null,
    systemCommentFailed,
    noteFailed,
    email,
  };
}
